import classNames from "classnames";
import styles from "../../../main/ObjectDetails/ObjectDetails.module.scss";
import { useTypedSelector } from "../../../../../hooks/useTypedSelector";
import { CustomLoader } from "../../../../../share/Loader/Loader";
import { useTranslation } from "react-i18next";
import { Check } from "../../../../../constants/icons";
import { getDomainFromAddress } from "../../../../../util/cyberSecurityUtil";

interface DetailsProps {
  contentClassName?: string;
}

const Details: React.FC<DetailsProps> = ({ contentClassName }) => {
  const { selectedObject: cybersecurity_company } = useTypedSelector(
    (state) => state.mapCybersecurityInfo
  );
  const { isLoading } = useTypedSelector((state) => state.auth);

  const { t: tDetails } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.objectDetails.details",
  });

  const getOtherServicesLabel = () => {
    const otherServices = cybersecurity_company.other_services;

    const { cybersecurity_percentage } = cybersecurity_company;

    if (otherServices.toLowerCase() === "профільна") {
      return tDetails("Profile");
    } else if (cybersecurity_percentage) {
      return tDetails("Percentage", { percentage: cybersecurity_percentage });
    }

    return otherServices;
  };

  const servicesList =
    cybersecurity_company && Array.isArray(cybersecurity_company.services)
      ? cybersecurity_company.services
      : [];

  const descriptionValue =
    cybersecurity_company?.description &&
    cybersecurity_company.description.trim().length > 0
      ? cybersecurity_company.description
      : tDetails("undefined");

  const linkedinValue = cybersecurity_company?.linkedin_company?.trim();
  const websiteValue = cybersecurity_company?.website?.trim();
  const emailValue = cybersecurity_company?.email?.trim();
  const phoneValues = Array.isArray(cybersecurity_company?.contacts)
    ? cybersecurity_company?.contacts
        .map((contact) => contact?.trim())
        .filter((contact): contact is string => Boolean(contact?.length))
    : [];

  const containerClassName = classNames(styles.detailsTab, contentClassName);

  return (
    <div>
      {isLoading ? (
        <CustomLoader />
      ) : (
        <div className={containerClassName}>
          <div className={styles.tabSection}>
            <h4 className={styles.tabTitle}>
              {tDetails("companyCharacteristicTitle")}
            </h4>
            <table className={styles.tabTable}>
              <tbody>
                <tr className={styles.tabRow}>
                  <td>{tDetails("edrpou")}:</td>
                  <td>
                    {cybersecurity_company?.edrpou ?? tDetails("undefined")}
                  </td>
                </tr>
                <tr className={styles.tabRow}>
                  <td>{tDetails("companyType")}:</td>
                  <td>
                    {cybersecurity_company?.company_type ??
                      tDetails("undefined")}
                  </td>
                </tr>
                <tr className={styles.tabRow}>
                  <td>{tDetails("companySize")}:</td>
                  <td>
                    {cybersecurity_company?.company_size ??
                      tDetails("undefined")}
                  </td>
                </tr>
                <tr className={styles.tabRow}>
                  <td>{tDetails("registrationDate")}:</td>
                  <td>
                    {cybersecurity_company?.registration_date ?? <span>-</span>}
                  </td>
                </tr>
                <tr className={styles.tabRow}>
                  <td>{tDetails("region")}:</td>
                  <td>
                    {cybersecurity_company?.region ?? tDetails("undefined")}
                  </td>
                </tr>

                <tr className={styles.tabRow}>
                  <td>{tDetails("linkedin")}:</td>
                  <td>
                    {linkedinValue ? (
                      <a
                        className={styles.detailsLink}
                        href={linkedinValue}
                        target="_blank"
                        rel="noreferrer"
                        title={linkedinValue}
                      >
                        {linkedinValue}
                      </a>
                    ) : (
                      tDetails("undefined")
                    )}
                  </td>
                </tr>

                {/* website */}
                <tr className={styles.tabRow}>
                  <td>{tDetails("website")}:</td>
                  <td>
                    {websiteValue ? (
                      <a
                        className={styles.detailsLink}
                        href={
                          websiteValue.startsWith("http")
                            ? websiteValue
                            : `https://${websiteValue}`
                        }
                        target="_blank"
                        rel="noreferrer"
                        title={websiteValue}
                      >
                        {getDomainFromAddress(websiteValue)}
                      </a>
                    ) : (
                      tDetails("undefined")
                    )}
                  </td>
                </tr>
                <tr className={styles.tabRow}>
                  <td>{tDetails("email")}:</td>
                  <td>
                    {emailValue ? (
                      <a
                        className={styles.detailsLink}
                        href={`mailto:${emailValue}`}
                        target="_blank"
                        rel="noreferrer"
                        title={emailValue}
                      >
                        {emailValue}
                      </a>
                    ) : (
                      tDetails("undefined")
                    )}
                  </td>
                </tr>
                <tr className={styles.tabRow}>
                  <td>{tDetails("phone")}:</td>
                  <td>
                    {phoneValues.length > 0 ? (
                      <div className={styles.contactLinks}>
                        {phoneValues.map((phoneNumber, index) => {
                          const phoneHref = phoneNumber.replace(/\s+/g, "");

                          return (
                            <a
                              key={`${phoneHref}-${index}`}
                              className={styles.detailsLink}
                              href={`tel:${phoneHref}`}
                              target="_blank"
                              rel="noreferrer"
                              title={phoneNumber}
                            >
                              {phoneNumber}
                            </a>
                          );
                        })}
                      </div>
                    ) : (
                      tDetails("undefined")
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={styles.tabSection}>
            <h4 className={styles.tabTitle}>
              {tDetails("companyActivityTitle")}
            </h4>
            <table className={styles.tabTable}>
              <tbody>
                <tr className={styles.tabRow}>
                  <td>{tDetails("certifications")}:</td>
                  <td>
                    {cybersecurity_company?.certifications
                      ? tDetails("yes")
                      : tDetails("no")}
                  </td>
                </tr>
                <tr className={styles.tabRow}>
                  <td>{tDetails("other_services")}:</td>
                  <td>{getOtherServicesLabel()}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={styles.tabSection}>
            <h4 className={styles.tabTitle}>{tDetails("servicesTitle")}</h4>
            {servicesList.length > 0 ? (
              <ul className={styles.servicesList}>
                {servicesList.map((service, index) => (
                  <li
                    className={styles.serviceItem}
                    key={`${service}-${index}`}
                  >
                    <img className={styles.serviceIcon} src={Check} alt="" />
                    <span className={styles.serviceText}>{service}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className={styles.tabText}>{tDetails("undefined")}</span>
            )}
          </div>

          <div className={styles.tabSection}>
            {cybersecurity_company && (
              <div className={styles.sectionDescription}>
                <h4 className={styles.tabTitle}>{tDetails("description")}</h4>
                <p className={styles.sectionDescriptionText}>
                  {descriptionValue}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Details;
